import React, { useState, useEffect } from 'react';
import { useData } from '../../../../hooks/useData';
import { useApp } from '../../../../contexts/AppContext';
import { TABLES } from '../../../../constants/supabaseConstants';
import { supabase } from '../../../../config/supabase';
import { createAutoProjectItem, MASTER_ITEM_IDS } from '../../../../utils/autoProjectItems';
import modalStyles from '../../../../styles/modals.module.css';

// Import formatBaseFloorName from SpacesStructure
import { formatBaseFloorName } from './SpacesStructure';

const SpaceFormModal = ({
  open,
  space = null,
  floorId = null,
  onClose,
  allFloors = [],
  onSubmitRef,
}) => {
  const { currentProject } = useApp();
  const [formData, setFormData] = useState({
    custom_space_name: space?.custom_space_name || '',
    floor_id: space?.floor_id || floorId || '',
    master_space_id: space?.master_space_id || '',
    room_size: space?.room_size || '',
    room_height: space?.room_height || '',
    description: space?.description || '',
    space_cost: space?.space_cost || 0, // Default for required field
    specifications: space?.specifications || {}, // Default for required field
  });

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [addDoor, setAddDoor] = useState(false);

  const { data: spaceTypes } = useData(TABLES.MASTER_SPACES);
  const { manageSpaces } = useData(TABLES.PROJECT_SPACES, {
    enabled: !!currentProject,
  });

  const isEditMode = !!space;

  // Get the selected space type to check if it's outdoor and has_door
  const selectedSpaceType = spaceTypes?.find(type => type.id === formData.master_space_id);

  // Update addDoor checkbox when space type changes (only for new spaces)
  useEffect(() => {
    if (!isEditMode && selectedSpaceType) {
      setAddDoor(!!selectedSpaceType.has_door);
    }
  }, [selectedSpaceType, isEditMode]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.master_space_id) {
      newErrors.master_space_id = 'נדרש סוג חלל';
    }

    if (!formData.floor_id) {
      newErrors.floor_id = 'נדרש מספר קומה';
    }

    if (formData.room_size && (isNaN(formData.room_size) || Number(formData.room_size) <= 0)) {
      newErrors.room_size = 'שטח חדר חייב להיות מספר חיובי';
    }

    if (
      formData.room_height &&
      (isNaN(formData.room_height) || Number(formData.room_height) <= 0)
    ) {
      newErrors.room_height = 'גובה חדר חייב להיות מספר חיובי';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async e => {
    if (e) e.preventDefault();

    const businessErrors = validateForm();
    if (!businessErrors) {
      return;
    }

    setIsLoading(true);
    try {
      // Prepare space data with proper types
      const spaceData = {
        ...formData,
        room_size: formData.room_size ? Number(formData.room_size) : null,
        room_height: formData.room_height ? Number(formData.room_height) : null,
        space_cost: Number(formData.space_cost),
        specifications: formData.specifications || {},
        floor_id: formData.floor_id, // Use floor_id to link to project
      };

      if (space?.id) {
        spaceData.id = space.id;
      }

      // Submit to database (exactly like floors)
      await manageSpaces([spaceData]);

      // If addDoor is checked, create a דלת פנים project_item for the new space
      if (!isEditMode && addDoor) {
        try {
          // Query for the space we just created (most recent match)
          const { data: newSpace } = await supabase
            .from('project_spaces')
            .select('id')
            .eq('floor_id', formData.floor_id)
            .eq('master_space_id', formData.master_space_id)
            .is('deleted_at', null)
            .order('created_at', { ascending: false })
            .limit(1)
            .single();

          if (newSpace) {
            await createAutoProjectItem(supabase, {
              projectId: currentProject.id,
              masterItemId: MASTER_ITEM_IDS.DOOR,
              spaceId: newSpace.id,
            });
          }
        } catch (doorError) {
          // Don't block space creation if door creation fails
          console.error('Error adding door to space:', doorError);
        }
      }

      onClose();
    } catch (error) {
      console.error('Error saving space:', error);
      setErrors(prev => ({
        ...prev,
        submit: 'שגיאה בשמירת החלל. אנא נסה שוב.',
      }));
    } finally {
      setIsLoading(false);
    }
  };

  // Register submit handler with ModalManager
  useEffect(() => {
    if (open && onSubmitRef) {
      onSubmitRef(() => handleSubmit);
    }
  }, [open, onSubmitRef, handleSubmit]);

  const handleChange = e => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when field is modified
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  if (!open) return null;

  return (
    <div className={modalStyles.overlay} onClick={onClose}>
      <div className={modalStyles.modal} dir="rtl" onClick={e => e.stopPropagation()}>
        <div className={modalStyles.header}>
          <h2 className={modalStyles.headerTitle}>{isEditMode ? 'ערוך חלל' : 'הוסף חלל חדש'}</h2>
        </div>

        <div className={modalStyles.content}>
          {errors.submit && <div className={modalStyles.error}>{errors.submit}</div>}

          <div className={modalStyles.formGrid}>
            <div className={modalStyles.field}>
              <label className={modalStyles.fieldLabel}>
                <span className={modalStyles.fieldRequired}>*</span>
                סוג חלל
              </label>
              <select
                className={modalStyles.fieldSelect}
                name="master_space_id"
                value={formData.master_space_id}
                onChange={handleChange}
                disabled={isLoading}
              >
                <option value="">בחר סוג חלל</option>
                {spaceTypes?.map(type => (
                  <option key={type.id} value={type.id}>
                    {type.name}
                  </option>
                ))}
              </select>
              {errors.master_space_id && (
                <div className={modalStyles.fieldError}>{errors.master_space_id}</div>
              )}
            </div>

            <div className={modalStyles.field}>
              <label className={modalStyles.fieldLabel}>
                שם חלל
                <span className={modalStyles.fieldOptional}>(אופציונלי)</span>
              </label>
              <input
                type="text"
                className={modalStyles.fieldInput}
                name="custom_space_name"
                value={formData.custom_space_name}
                onChange={handleChange}
              />
            </div>

            <div className={modalStyles.field}>
              <label className={modalStyles.fieldLabel}>
                <span className={modalStyles.fieldRequired}>*</span>
                קומה
              </label>
              <select
                className={modalStyles.fieldSelect}
                name="floor_id"
                value={formData.floor_id}
                onChange={handleChange}
              >
                <option value="">בחר קומה</option>
                {allFloors.map(floor => (
                  <option key={floor.id} value={floor.id}>
                    {formatBaseFloorName(floor.floor_number)}
                  </option>
                ))}
              </select>
              {errors.floor_id && <div className={modalStyles.fieldError}>{errors.floor_id}</div>}
            </div>

            <div className={modalStyles.formRow}>
              <div className={modalStyles.field}>
                <label className={modalStyles.fieldLabel}>גודל חלל (מ&quot;ר)</label>
                <input
                  type="number"
                  className={modalStyles.fieldInput}
                  name="room_size"
                  value={formData.room_size}
                  onChange={handleChange}
                  min="0"
                  step="0.1"
                />
                {errors.room_size && (
                  <div className={modalStyles.fieldError}>{errors.room_size}</div>
                )}
              </div>

              {selectedSpaceType?.space_category !== 'חוץ' && (
                <div className={modalStyles.field}>
                  <label className={modalStyles.fieldLabel}>גובה חלל (מ&apos;)</label>
                  <input
                    type="number"
                    className={modalStyles.fieldInput}
                    name="room_height"
                    value={formData.room_height}
                    onChange={handleChange}
                    min="0"
                    step="0.1"
                  />
                  {errors.room_height && (
                    <div className={modalStyles.fieldError}>{errors.room_height}</div>
                  )}
                </div>
              )}
            </div>

            <div className={modalStyles.field}>
              <label className={modalStyles.fieldLabel}>
                תיאור
                <span className={modalStyles.fieldOptional}>(אופציונלי)</span>
              </label>
              <textarea
                className={modalStyles.fieldTextarea}
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={4}
              />
            </div>
          </div>

          {/* Indoor door checkbox - only for new spaces with has_door space type */}
          {!isEditMode && selectedSpaceType?.has_door && (
            <div className={modalStyles.field} style={{ marginTop: '8px' }}>
              <label
                style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}
              >
                <input
                  type="checkbox"
                  checked={addDoor}
                  onChange={e => setAddDoor(e.target.checked)}
                />
                <span>הוסף דלת פנים לחלל</span>
              </label>
            </div>
          )}
        </div>

        <div className={modalStyles.footer}>
          <button onClick={onClose} className={modalStyles.cancelButton} disabled={isLoading}>
            ביטול
          </button>
          <button onClick={handleSubmit} className={modalStyles.primaryButton} disabled={isLoading}>
            {isLoading ? 'שומר...' : isEditMode ? 'שמור' : 'הוסף'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SpaceFormModal;
